import { forwardRef, useEffect, useImperativeHandle, useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { acceptForUploadKind, isAllowedUploadFile, type UploadKind } from '@lub0v/santa-utils';
import { showDialog } from '@src/components/Dialog/Dialog';

export interface UploadedFile {
  filename: string;
  url: string;
  key: string;
  size: number;
}

export type FileUploadState = 'selected' | 'uploaded' | 'error';

export interface FileUploadProps {
  onChange: (state: FileUploadState, file: UploadedFile | null) => void;
  maxSize?: number;
  kind?: UploadKind;
  accept?: string;
  tooLargeMessage?: string;
}

export interface FileUploadHandle {
  open: () => void;
}

/** Default cap for image uploads in the new-box and settings pickers. */
export const IMAGE_UPLOAD_MAX_SIZE = 1024 * 1024;

function parseUploadResult(data: unknown): { publicUrl: string; key: string } {
  if (data && typeof data === 'object' && 'publicUrl' in data && 'key' in data) {
    const upload = data as { publicUrl: string; key: string };
    if (upload.publicUrl && upload.key) {
      return upload;
    }
  }
  throw new Error('Invalid upload response');
}

/** Hidden file input — call `open()` to show the native picker, same flow as v1. */
export const FileUpload = forwardRef<FileUploadHandle, FileUploadProps>(function FileUpload(
  { onChange, maxSize = IMAGE_UPLOAD_MAX_SIZE, kind = 'attachment', accept, tooLargeMessage },
  ref,
) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<AbortController | null>(null);
  useEffect(() => () => uploadRef.current?.abort(), []);

  useImperativeHandle(ref, () => ({
    open: () => {
      inputRef.current?.click();
    },
  }));

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const loadedFile = event.target.files?.[0];
    event.target.value = '';
    if (!loadedFile) {
      return;
    }

    if (loadedFile.size >= maxSize) {
      showDialog({
        title: t('upload.tooLarge.title'),
        children: tooLargeMessage || t('upload.tooLarge.body'),
        submit: t('dialog.ok'),
        testId: 'upload-too-large',
      });
      return;
    }

    if (!isAllowedUploadFile(loadedFile.name, loadedFile.type, kind)) {
      showDialog({
        title: t('upload.unsupported.title'),
        children: t(kind === 'image' ? 'upload.unsupported.image' : 'upload.unsupported.file'),
        submit: t('dialog.ok'),
        testId: 'upload-unsupported',
      });
      return;
    }

    uploadRef.current?.abort();
    const controller = new AbortController();
    uploadRef.current = controller;
    try {
      onChange('selected', {
        filename: loadedFile.name,
        url: '',
        key: '',
        size: loadedFile.size,
      });
      const res = await fetch('/api/upload', {
        method: 'POST',
        signal: controller.signal,
        credentials: 'include',
        headers: {
          'Content-Type': loadedFile.type || 'application/octet-stream',
          // HTTP headers require ASCII; the backend uses this name only for extension validation.
          'X-Filename': encodeURIComponent(loadedFile.name),
          'X-Upload-Kind': kind,
        },
        body: loadedFile,
      });
      if (!res.ok) {
        throw new Error('upload failed');
      }
      const upload = parseUploadResult(await res.json());
      if (controller.signal.aborted) return;
      onChange('uploaded', {
        filename: loadedFile.name,
        url: upload.publicUrl,
        key: upload.key,
        size: loadedFile.size,
      });
    } catch {
      if (!controller.signal.aborted) onChange('error', null);
    }
  }

  return (
    <input
      ref={inputRef}
      type="file"
      accept={accept || acceptForUploadKind(kind)}
      onChange={(event) => void handleFileChange(event)}
      hidden
    />
  );
});
